/**
 * xml-schema — pure XML serialise/parse for the CustomXmlPart root
 * `urn:theplaidoyer:docx:1` v="1".
 *
 * Schema LOCKED per plan §3.
 *
 * Pure module — no Office.js, no React. Lives in `src/lib/` for
 * framework-free utility consumption by both domain and adapter.
 *
 * Parses via the browser DOM (jsdom in tests, native in Word
 * runtime). Office.js doesn't expose an XML parser of its own; we
 * keep this in lib/ so it can be unit-tested without Word.
 */

export const XML_NAMESPACE = 'urn:theplaidoyer:docx:1'
export const XML_VERSION = '1'

import type { ChipStatus } from '@/domain/citation-chip/entity'

export interface XmlCitation {
  cid: string
  refKey: string
  status: ChipStatus
  score: number
  insertedAt: string
  label: string
  codeName: string
  articleNumber: string
}

export interface XmlPiece {
  id: string
  pages?: number
  usedAtParagraphs?: string
}

/** Doc-level metadata held in the CustomXmlPart. Mirror of the
 *  `<theplaidoyer>` root contents per plan §3.1. */
export interface DocumentMetadata {
  briefId: string
  caseId: string
  snapshotVersion: number
  lang: 'fr' | 'ar' | 'en'
  styleProfile: string
  lastSyncAt: string
  /** SHA-256 hex of the "Diff 0" content snapshot. Empty until M5. */
  diffHash: string
  citations: XmlCitation[]
  bordereau: XmlPiece[]
}

export type XmlParseErrorKind =
  | 'wrong_namespace'
  | 'wrong_version'
  | 'invalid_structure'
  | 'invalid_cite'

export class XmlParseError extends Error {
  constructor(
    public readonly kind: XmlParseErrorKind,
    message: string,
  ) {
    super(message)
    this.name = 'XmlParseError'
  }
}

// ─── Serialise ──────────────────────────────────────────────────────

/** Escape a string for inclusion in an XML attribute value. */
function escapeAttr(v: string): string {
  return v
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')
}

/** Serialise a DocumentMetadata into the `urn:theplaidoyer:docx:1` v="1" XML. */
export function serialiseXmlPart(meta: DocumentMetadata): string {
  const lines: string[] = []
  lines.push('<?xml version="1.0" encoding="UTF-8"?>')
  lines.push(`<theplaidoyer xmlns="${XML_NAMESPACE}" v="${XML_VERSION}">`)
  lines.push(
    `  <doc briefId="${escapeAttr(meta.briefId)}" caseId="${escapeAttr(meta.caseId)}" snapshotVersion="${meta.snapshotVersion}"/>`,
  )
  lines.push(`  <lang code="${escapeAttr(meta.lang)}"/>`)
  lines.push(`  <style profile="${escapeAttr(meta.styleProfile)}"/>`)
  lines.push(
    `  <sync lastSyncAt="${escapeAttr(meta.lastSyncAt)}" diffHash="${escapeAttr(meta.diffHash)}"/>`,
  )
  if (meta.citations.length === 0) {
    lines.push('  <citations></citations>')
  } else {
    lines.push('  <citations>')
    for (const cite of meta.citations) {
      lines.push(
        `    <cite cid="${escapeAttr(cite.cid)}" refKey="${escapeAttr(cite.refKey)}" status="${escapeAttr(cite.status)}" score="${cite.score.toFixed(2)}" insertedAt="${escapeAttr(cite.insertedAt)}" label="${escapeAttr(cite.label)}" codeName="${escapeAttr(cite.codeName)}" articleNumber="${escapeAttr(cite.articleNumber)}"/>`,
      )
    }
    lines.push('  </citations>')
  }
  if (meta.bordereau.length === 0) {
    lines.push('  <bordereau></bordereau>')
  } else {
    lines.push('  <bordereau>')
    for (const piece of meta.bordereau) {
      const attrs = [
        `id="${escapeAttr(piece.id)}"`,
        piece.pages !== undefined ? `pages="${piece.pages}"` : 'pages=""',
        `usedAtParagraphs="${escapeAttr(piece.usedAtParagraphs ?? '')}"`,
      ].join(' ')
      lines.push(`    <piece ${attrs}/>`)
    }
    lines.push('  </bordereau>')
  }
  lines.push('</theplaidoyer>')
  return lines.join('\n')
}

// ─── Parse ──────────────────────────────────────────────────────────

const STATUS_VALUES = new Set<ChipStatus>([
  'pending',
  'verified',
  'unverified',
  'stale',
  'broken-anchor',
])

/** Parse the `urn:theplaidoyer:docx:1` XML into a typed DocumentMetadata. */
export function parseXmlPart(xml: string): DocumentMetadata {
  const parser = new DOMParser()
  const doc = parser.parseFromString(xml, 'application/xml')

  // DOMParser doesn't throw on malformed XML — it returns a doc with a
  // `<parsererror>` element. Detect that.
  const parserError = doc.getElementsByTagName('parsererror')
  if (parserError.length > 0) {
    throw new XmlParseError(
      'invalid_structure',
      `XML parse failed: ${parserError[0].textContent ?? '(no detail)'}`,
    )
  }

  const root = doc.documentElement
  if (!root || root.localName !== 'theplaidoyer') {
    throw new XmlParseError(
      'invalid_structure',
      `expected root <theplaidoyer>, got <${root?.localName ?? '(none)'}>`,
    )
  }

  const ns = root.getAttribute('xmlns')
  if (ns !== XML_NAMESPACE) {
    throw new XmlParseError(
      'wrong_namespace',
      `expected xmlns="${XML_NAMESPACE}", got "${ns ?? '(none)'}"`,
    )
  }

  const version = root.getAttribute('v')
  if (version !== XML_VERSION) {
    throw new XmlParseError(
      'wrong_version',
      `expected v="${XML_VERSION}", got v="${version ?? '(none)'}"`,
    )
  }

  const docEl = root.getElementsByTagName('doc')[0]
  if (!docEl) {
    throw new XmlParseError(
      'invalid_structure',
      'missing required <doc> element',
    )
  }
  const briefId = docEl.getAttribute('briefId') ?? ''
  const caseId = docEl.getAttribute('caseId') ?? ''
  const snapshotVersion = parseInt(
    docEl.getAttribute('snapshotVersion') ?? '0',
    10,
  )

  const langEl = root.getElementsByTagName('lang')[0]
  const langRaw = langEl?.getAttribute('code') ?? 'fr'
  if (langRaw !== 'fr' && langRaw !== 'ar' && langRaw !== 'en') {
    throw new XmlParseError(
      'invalid_structure',
      `lang/@code must be fr/ar/en, got '${langRaw}'`,
    )
  }
  const lang = langRaw as 'fr' | 'ar' | 'en'

  const styleEl = root.getElementsByTagName('style')[0]
  const styleProfile = styleEl?.getAttribute('profile') ?? 'fr-fr-nf-z44-005'

  const syncEl = root.getElementsByTagName('sync')[0]
  const lastSyncAt =
    syncEl?.getAttribute('lastSyncAt') ?? '1970-01-01T00:00:00Z'
  const diffHash = syncEl?.getAttribute('diffHash') ?? ''

  // Parse citations — dedupe by cid (first wins).
  const citationsEl = root.getElementsByTagName('citations')[0]
  const citations: XmlCitation[] = []
  if (citationsEl) {
    const seen = new Set<string>()
    for (let i = 0; i < citationsEl.childNodes.length; i += 1) {
      const node = citationsEl.childNodes[i]
      if (node.nodeType !== 1 /* ELEMENT_NODE */) continue
      const el = node as Element
      if (el.localName !== 'cite') {
        throw new XmlParseError(
          'invalid_structure',
          `<citations> may only contain <cite>, found <${el.localName}>`,
        )
      }
      const cid = el.getAttribute('cid')
      if (!cid) {
        throw new XmlParseError('invalid_cite', 'cite/@cid is required')
      }
      if (seen.has(cid)) continue // dedupe — first wins
      seen.add(cid)
      const refKey = el.getAttribute('refKey') ?? ''
      const statusRaw = el.getAttribute('status') ?? 'pending'
      if (!STATUS_VALUES.has(statusRaw as ChipStatus)) {
        throw new XmlParseError(
          'invalid_cite',
          `cite/@status invalid: '${statusRaw}'`,
        )
      }
      const status = statusRaw as ChipStatus
      const scoreRaw = el.getAttribute('score') ?? '0.00'
      const score = parseFloat(scoreRaw)
      if (!Number.isFinite(score) || score < 0 || score > 1) {
        throw new XmlParseError(
          'invalid_cite',
          `cite/@score invalid: '${scoreRaw}'`,
        )
      }
      const insertedAt = el.getAttribute('insertedAt') ?? '1970-01-01T00:00:00Z'
      const label = el.getAttribute('label') ?? ''
      const codeName = el.getAttribute('codeName') ?? ''
      const articleNumber = el.getAttribute('articleNumber') ?? ''
      citations.push({
        cid,
        refKey,
        status,
        score,
        insertedAt,
        label,
        codeName,
        articleNumber,
      })
    }
  }

  const bordereauEl = root.getElementsByTagName('bordereau')[0]
  const bordereau: XmlPiece[] = []
  if (bordereauEl) {
    for (let i = 0; i < bordereauEl.childNodes.length; i += 1) {
      const node = bordereauEl.childNodes[i]
      if (node.nodeType !== 1) continue
      const el = node as Element
      if (el.localName !== 'piece') {
        throw new XmlParseError(
          'invalid_structure',
          `<bordereau> may only contain <piece>, found <${el.localName}>`,
        )
      }
      const id = el.getAttribute('id') ?? ''
      const pagesRaw = el.getAttribute('pages')
      const pages =
        pagesRaw && pagesRaw !== '' ? parseInt(pagesRaw, 10) : undefined
      const usedAtParagraphs = el.getAttribute('usedAtParagraphs') ?? ''
      bordereau.push({ id, pages, usedAtParagraphs })
    }
  }

  return {
    briefId,
    caseId,
    snapshotVersion,
    lang,
    styleProfile,
    lastSyncAt,
    diffHash,
    citations,
    bordereau,
  }
}

// ─── Migration stub ────────────────────────────────────────────────

/** v1 → v2 migration. Today returns input unchanged (no-op stub).
 *
 *  Structure deliberately on-disk so future contributors are forced
 *  to update this when introducing v2. */
export function migrateV1ToV2(input: DocumentMetadata): DocumentMetadata {
  return input
}
